import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getPaymentMethods } from '../../api'
import { JsonPaymentMethod, PaymentMethod, RecordStatus } from '../../models'
import { RecordStatusFilters } from '../records/filters'

interface FilterProps {
    updateFilters: (
        filterName: string,
        newValue: string,
        shouldDelete?: boolean,
        dependentFilterToClear?: string
    ) => void
}

const parsePaymentMethodParam = (value: string | null): string[] => {
    if (!value) {
        return []
    }

    return value
        .split(',')
        .map((methodId) => methodId.trim())
        .filter(
            (methodId, index, source) =>
                methodId !== '' && source.indexOf(methodId) === index
        )
}

export const PaymentFilters = (props: FilterProps) => {
    const { updateFilters } = props
    const [searchParams, setSearchParams] = useSearchParams()

    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
    const isCheckMethod = (method: PaymentMethod) =>
        PaymentMethod.CHECK_METHODS.includes(method.id)
    const selectedPaymentMethods = parsePaymentMethodParam(
        searchParams.get('payment_method_id')
    )
    const dateFrom = searchParams.get('date_paid_from') ?? ''
    const dateTo = searchParams.get('date_paid_to') ?? ''
    const checkNumberFromParams = searchParams.get('check_number') ?? ''
    const [checkNumberInput, setCheckNumberInput] = useState(checkNumberFromParams)

    type Timer = ReturnType<typeof setTimeout>
    const timeoutRef = useRef<Timer>()

    const serializePaymentMethods = (methods: string[]) => methods.join(',')

    const updatePaymentMethodFilter = (methods: string[]) => {
        const serializedValue = serializePaymentMethods(methods)
        const nextSearchParams = new URLSearchParams(searchParams)

        if (serializedValue === '') {
            nextSearchParams.delete('payment_method_id')
        } else {
            nextSearchParams.set('payment_method_id', serializedValue)
        }

        if (nextSearchParams.toString() !== searchParams.toString()) {
            setSearchParams(nextSearchParams)
        }

        updateFilters('payment_method_id', serializedValue, serializedValue === '')
    }

    const selectedCheckMethod = paymentMethods.find(
        (method: PaymentMethod) =>
            isCheckMethod(method) &&
            selectedPaymentMethods.includes(String(method.id))
    )
    const showCheckNumberInput = Boolean(selectedCheckMethod)

    useEffect(() => {
        getPaymentMethods()
            .then((json) => {
                setPaymentMethods(
                    json.data.map(
                        (method: JsonPaymentMethod) => new PaymentMethod(method)
                    )
                )
            })
    }, [])

    useEffect(() => {
        setCheckNumberInput(checkNumberFromParams)
    }, [checkNumberFromParams])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    // Filter to show only paid statuses for payments page
    const paidStatusFilter = (status: RecordStatus) => {
        return RecordStatus.PAID_STATUSES.includes(status.code)
    }

    const handleCheckboxChange = (value: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValues = e.target.checked
            ? [...selectedPaymentMethods.filter((id) => id !== 'none'), value]
            : selectedPaymentMethods.filter((id) => id !== value)

        updatePaymentMethodFilter(newValues)
    }

    return (
        <div className="row g-2">
            <div className="col-md-12">
                <label htmlFor='paymentMethodFilter'>Payment Method</label>
                <div className="row">
                    <div className='col'>
                        <div className="form-check">
                            <input
                            type="checkbox"
                            className="form-check-input"
                            id="paymentMethodAll"
                            checked={selectedPaymentMethods.length === 0}
                            onChange={() => {
                                updatePaymentMethodFilter([])
                            }}
                            />
                            <label className="form-check-label" htmlFor="paymentMethodAll">
                            All
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="paymentMethodNone"
                                checked={selectedPaymentMethods.length === 1 && selectedPaymentMethods[0] === 'none'}
                                onChange={(e) => {
                                const newValues = e.target.checked ? ['none'] : []

                                updatePaymentMethodFilter(newValues)
                                }}
                            />
                            <label className="form-check-label" htmlFor="paymentMethodNone">
                                No Payment Method
                            </label>
                        </div>
                    </div>
                    <div className="row">
                        {paymentMethods.map((method: PaymentMethod) => {
                            const methodId = String(method.id)
                            const isChecked = selectedPaymentMethods.includes(methodId)

                            return (
                                <div className="col-md-6" key={method.id}>
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`paymentMethod-${method.id}`}
                                            checked={isChecked}
                                            onChange={handleCheckboxChange(methodId)}
                                        />
                                        <label className="form-check-label">
                                            {method.name}
                                        </label>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    {showCheckNumberInput && (
                        <div className="col-md-6 mt-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Check Number"
                                value={checkNumberInput}
                                onChange={(e) => {
                                    const value = e.target.value

                                    setCheckNumberInput(value)

                                    if (timeoutRef.current) {
                                        clearTimeout(timeoutRef.current)
                                    }

                                    timeoutRef.current = setTimeout(
                                        (val: string) => {
                                            const nextSearchParams = new URLSearchParams(searchParams)

                                            if (val === '') {
                                                nextSearchParams.delete('check_number')
                                            } else {
                                                nextSearchParams.set('check_number', val)
                                            }

                                            if (nextSearchParams.toString() !== searchParams.toString()) {
                                                setSearchParams(nextSearchParams)
                                            }

                                            updateFilters('check_number', val, val === '')
                                        },
                                        500,
                                        value
                                    )
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="col-12">
                <RecordStatusFilters
                    updateFilters={updateFilters}
                    statusFilter={paidStatusFilter}
                />
            </div>
            <div className="col-md-6">
                <label htmlFor='paymentDateFromFilter'>
                    Payment Date From
                </label>
                <input
                    className='form-control'
                    id='paymentDateFromFilter'
                    value={dateFrom}
                    onChange={(e) => {
                        const value = e.target.value
                        const nextSearchParams = new URLSearchParams(searchParams)

                        if (value === '') {
                            nextSearchParams.delete('date_paid_from')
                        } else {
                            nextSearchParams.set('date_paid_from', value)
                        }

                        if (nextSearchParams.toString() !== searchParams.toString()) {
                            setSearchParams(nextSearchParams)
                        }

                        updateFilters('date_paid_from', e.target.value, e.target.value === '')
                    }}
                    type='date'
                    max={dateTo} />
            </div>
            <div className="col-md-6">
                <label htmlFor='paymentDateToFilter'>Payment Date To</label>
                <input
                    className='form-control'
                    id='paymentDateToFilter'
                    value={dateTo}
                    onChange={(e) => {
                        const value = e.target.value
                        const nextSearchParams = new URLSearchParams(searchParams)

                        if (value === '') {
                            nextSearchParams.delete('date_paid_to')
                        } else {
                            nextSearchParams.set('date_paid_to', value)
                        }

                        if (nextSearchParams.toString() !== searchParams.toString()) {
                            setSearchParams(nextSearchParams)
                        }

                        updateFilters('date_paid_to', e.target.value, e.target.value === '')
                    }}
                    type='date'
                    min={dateFrom} />
            </div>
        </div>
    )
}
